class MegaMenuCarousel {
  constructor(root, blockId) {
    this.root = root;
    this.blockId = blockId;
    this.wrapper = root.querySelector(`[data-mega-menu-trigger-wrapper="dropdown-${blockId}"]`);
    this.shell = root.querySelector(`[data-desktop-scroll-shell="${blockId}"]`);
    this.blocksWrapper = root.querySelector(`[data-blocks-wrapper="${blockId}"]`);
    this.prevButton = root.querySelector(`[data-scroll-prev="${blockId}"]`);
    this.nextButton = root.querySelector(`[data-scroll-next="${blockId}"]`);
    this.scrollContainer = root.querySelector(`[data-scroll-container="${blockId}"]`);
    this.desktopTarget = root.querySelector(`[data-menu-blocks-target="desktop-${blockId}"]`);
    this.mobileTarget = root.querySelector(`[data-menu-blocks-target="mobile-${blockId}"]`);
    this.currentOffset = 0;
    this.maxOffset = 0;
    this.resizeFrame = null;

    this.handleResize = this.onResize.bind(this);
    this.handlePrevClick = this.onPrevClick.bind(this);
    this.handleNextClick = this.onNextClick.bind(this);
    this.handleDropdownClose = this.resetCarousel.bind(this);
  }

  init() {
    if (!this.wrapper || !this.blocksWrapper || !this.desktopTarget || !this.scrollContainer) {
      return false;
    }

    this.cloneMenuItems();
    this.bindEvents();
    this.measureOverflow();
    return true;
  }

  destroy() {
    window.removeEventListener('resize', this.handleResize);
    this.prevButton?.removeEventListener('click', this.handlePrevClick);
    this.nextButton?.removeEventListener('click', this.handleNextClick);
    this.wrapper?.removeEventListener('mega-menu:dropdown-close', this.handleDropdownClose);

    if (this.resizeFrame) {
      window.cancelAnimationFrame(this.resizeFrame);
    }
  }

  cloneMenuItems() {
    if (!this.desktopTarget.dataset.cloned) {
      this.blocksWrapper.querySelectorAll('.mega-menu-item').forEach((block) => {
        this.desktopTarget.appendChild(block.cloneNode(true));
      });
      this.desktopTarget.dataset.cloned = 'true';
    }

    if (this.mobileTarget && !this.mobileTarget.dataset.cloned) {
      this.blocksWrapper.querySelectorAll('.mobile-submenu-item-content').forEach((block) => {
        const mobileClone = block.cloneNode(true);
        mobileClone.classList.remove('hidden');
        mobileClone.classList.add('flex');
        this.mobileTarget.appendChild(mobileClone);
      });
      this.mobileTarget.dataset.cloned = 'true';
    }
  }

  bindEvents() {
    this.prevButton?.addEventListener('click', this.handlePrevClick);
    this.nextButton?.addEventListener('click', this.handleNextClick);
    this.wrapper?.addEventListener('mega-menu:dropdown-close', this.handleDropdownClose);
    window.addEventListener('resize', this.handleResize);
  }

  getGap() {
    const styles = window.getComputedStyle(this.desktopTarget);
    return parseFloat(styles.columnGap || styles.gap || 0);
  }

  getItemStep() {
    const firstItem = this.desktopTarget.children[0];
    if (!firstItem) return this.scrollContainer.clientWidth;

    return firstItem.getBoundingClientRect().width + this.getGap();
  }

  resetViewportWidth() {
    this.scrollContainer.style.maxWidth = '';
  }

  getWholeItemViewportWidth(availableWidth, contentWidth) {
    const itemStep = this.getItemStep();
    const gap = this.getGap();

    if (!itemStep) return availableWidth;

    const visibleItemCount = Math.max(1, Math.floor((availableWidth + gap) / itemStep));
    const wholeItemWidth = visibleItemCount * itemStep - gap;

    return Math.min(contentWidth, wholeItemWidth);
  }

  setViewportWidth(width) {
    this.scrollContainer.style.maxWidth = width > 0 ? `${width}px` : '';
  }

  setArrowGutters(shouldShow) {
    if (!this.shell || !this.prevButton || !this.nextButton) return;

    if (!shouldShow) {
      this.shell.style.paddingLeft = '';
      this.shell.style.paddingRight = '';
      this.prevButton.classList.add('hidden');
      this.nextButton.classList.add('hidden');
      return;
    }

    const prevWidth = this.prevButton.querySelector('svg')?.getBoundingClientRect().width || 63;
    const nextWidth = this.nextButton.querySelector('svg')?.getBoundingClientRect().width || 63;
    const arrowGutter = Math.ceil(Math.max(prevWidth, nextWidth, 63) + 16);

    this.shell.style.paddingLeft = `${arrowGutter}px`;
    this.shell.style.paddingRight = `${arrowGutter}px`;
    this.prevButton.classList.remove('hidden');
    this.nextButton.classList.remove('hidden');
  }

  setButtonDisabledState(button, isDisabled) {
    if (!button) return;

    const icon = button.querySelector('svg');
    const circle = button.querySelector('circle');
    const path = button.querySelector('path');

    button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
    button.tabIndex = isDisabled ? -1 : 0;
    button.classList.toggle('opacity-50', isDisabled);
    button.classList.toggle('cursor-not-allowed', isDisabled);

    if (icon) {
      icon.classList.toggle('opacity-60', isDisabled);
    }

    if (circle) {
      circle.setAttribute('fill-opacity', isDisabled ? '0.1' : '0.2');
    }

    if (path) {
      path.setAttribute('stroke-opacity', isDisabled ? '0.25' : '0.5');
    }
  }

  isButtonDisabled(button) {
    return button?.getAttribute('aria-disabled') === 'true';
  }

  updateButtons() {
    if (!this.prevButton || !this.nextButton) return;

    if (this.maxOffset <= 0) {
      this.currentOffset = 0;
      this.desktopTarget.style.transform = 'translateX(0)';
      this.setButtonDisabledState(this.prevButton, true);
      this.setButtonDisabledState(this.nextButton, true);
      this.setArrowGutters(false);
      return;
    }

    this.currentOffset = Math.min(this.currentOffset, this.maxOffset);
    this.desktopTarget.style.transform = `translateX(-${this.currentOffset}px)`;
    this.setButtonDisabledState(this.prevButton, this.currentOffset <= 0);
    this.setButtonDisabledState(this.nextButton, this.currentOffset >= this.maxOffset);
    this.setArrowGutters(true);
  }

  measureOverflow() {
    this.resetViewportWidth();
    this.setArrowGutters(false);
    this.desktopTarget.style.transform = `translateX(-${this.currentOffset}px)`;

    const contentWidth = Math.ceil(this.desktopTarget.scrollWidth);
    let availableWidth = Math.floor(this.scrollContainer.clientWidth);

    if (contentWidth > availableWidth && this.prevButton && this.nextButton) {
      this.setArrowGutters(true);
      availableWidth = Math.floor(this.scrollContainer.clientWidth);
      this.setViewportWidth(this.getWholeItemViewportWidth(availableWidth, contentWidth));
      availableWidth = Math.floor(this.scrollContainer.clientWidth);
    }

    this.maxOffset = Math.max(0, contentWidth - availableWidth);
    this.updateButtons();
  }

  onResize() {
    if (this.resizeFrame) {
      window.cancelAnimationFrame(this.resizeFrame);
    }

    this.resizeFrame = window.requestAnimationFrame(() => {
      this.measureOverflow();
    });
  }

  resetCarousel() {
    this.currentOffset = 0;
    this.desktopTarget.style.transform = 'translateX(0)';
    this.measureOverflow();
  }

  onNextClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const itemStep = this.getItemStep();
    if (this.isButtonDisabled(this.nextButton) || !itemStep) return;

    this.currentOffset = Math.min(this.currentOffset + itemStep, this.maxOffset);
    this.updateButtons();
  }

  onPrevClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const itemStep = this.getItemStep();
    if (this.isButtonDisabled(this.prevButton) || !itemStep) return;

    this.currentOffset = Math.max(this.currentOffset - itemStep, 0);
    this.updateButtons();
  }
}

class MegaMenu extends HTMLElement {
  constructor() {
    super();
    this.triggerMode = 'hover';
    this.activeSubmenuId = null;
    this.carousels = [];
  }

  connectedCallback() {
    this.triggerMode = this.getAttribute('data-trigger-mode') || 'hover';
    this.headerBreakpoint = Number(this.getAttribute('data-header-breakpoint')) || 990;
    this.wrappers = this.querySelectorAll('[data-mega-menu-trigger-wrapper]');

    this.mobileMenuMain = this.querySelector('[data-mobile-menu-main]');
    this.mobileSubmenuTriggers = this.querySelectorAll('[data-mobile-submenu-trigger]');
    this.hamburgerIcon = this.querySelector('.mobile-menu-hamburger');
    this.backIcon = this.querySelector('.mobile-menu-back');
    this.detailsElement = this.querySelector('[data-mobile-menu-drawer]');
    this.summaryElement = this.detailsElement?.querySelector('summary');
    this.summaryLabel = this.summaryElement?.querySelector('[data-mobile-menu-label]');

    this.bindEvents();
    this.bindMobileEvents();
    this.initCarousels();
    this.updateMobileMenuIcons();
    this.updateMobileMenuLabel();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  bindEvents() {
    this.handleKeyDown = this.onKeyDown.bind(this);
    this.handleMobileEscape = this.onMobileEscape.bind(this);
    this.handleClickOutside = this.onClickOutside.bind(this);

    document.addEventListener('keydown', this.handleKeyDown);
    this.addEventListener('keyup', this.handleMobileEscape, true);
    document.addEventListener('click', this.handleClickOutside);

    this.wrappers.forEach(wrapper => {
      const trigger = wrapper.querySelector('[data-mega-menu-trigger]');
      const dropdown = wrapper.querySelector('[data-mega-menu-dropdown]');
      const mode = wrapper.getAttribute('data-trigger-mode') || this.triggerMode;
      const mobileTriggerMode = wrapper.getAttribute('data-mobile-trigger-mode');

      if (!trigger || !dropdown) return;

      if (mobileTriggerMode === 'click' && mode !== 'click') {
        trigger.addEventListener('click', (e) => {
          if (this.isDesktopViewport()) return;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.toggleDropdown(wrapper, trigger, dropdown);
        });
      }

      if (mode === 'click') {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleDropdown(wrapper, trigger, dropdown);
        });
      }

      if (mode === 'hover') {
        wrapper.addEventListener('mouseenter', () => {
          this.openDropdown(wrapper, trigger, dropdown);
        });

        wrapper.addEventListener('mouseleave', () => {
          this.closeDropdown(wrapper, trigger, dropdown);
        });
      }

      wrapper.addEventListener('focusin', () => {
        if (mode === 'hover' || this.isKeyboardFocus(trigger)) {
          this.openDropdown(wrapper, trigger, dropdown);
        }
      });

      wrapper.addEventListener('focusout', (e) => {
        if (!wrapper.contains(e.relatedTarget)) {
          this.closeDropdown(wrapper, trigger, dropdown);
        }
      });
    });
  }

  bindMobileEvents() {
    this.mobileSubmenuTriggers.forEach((trigger) => {
      const submenuId = trigger.getAttribute('data-mobile-submenu-trigger');
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.openMobileSubmenu(submenuId);
      }, true);
    });

    if (this.summaryElement) {
      this.summaryElement.addEventListener('click', (e) => {
        if (this.detailsElement.hasAttribute('open') && this.activeSubmenuId) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.closeMobileSubmenu();
        }
      }, true);
    }

    if (this.detailsElement) {
      this.detailsElement.addEventListener('toggle', () => {
        if (!this.detailsElement.open) {
          this.closeMobileSubmenu();
          this.closeAllDropdowns();
        }

        this.updateMobileMenuIcons();
        this.updateMobileMenuLabel();
      });
    }
  }

  unbindEvents() {
    document.removeEventListener('keydown', this.handleKeyDown);
    this.removeEventListener('keyup', this.handleMobileEscape, true);
    document.removeEventListener('click', this.handleClickOutside);
    this.carousels.forEach((carousel) => carousel.destroy());
    this.carousels = [];
  }

  initCarousels() {
    this.querySelectorAll('[data-blocks-wrapper]').forEach((blocksWrapper) => {
      const blockId = blocksWrapper.getAttribute('data-blocks-wrapper');
      const carousel = new MegaMenuCarousel(this, blockId);

      if (carousel.init()) {
        this.carousels.push(carousel);
      }
    });
  }

  isDesktopViewport() {
    return window.innerWidth >= this.headerBreakpoint;
  }

  isKeyboardFocus(element) {
    try {
      return element.matches(':focus-visible') || element.classList.contains('focused');
    } catch (error) {
      return element.classList.contains('focused');
    }
  }

  onKeyDown(event) {
    if (event.key !== 'Escape' || this.activeSubmenuId) return;

    this.closeAllDropdowns();
    const activeElement = document.activeElement;
    const wrapper = activeElement?.closest('[data-mega-menu-trigger-wrapper]');
    if (wrapper) {
      const trigger = wrapper.querySelector('[data-mega-menu-trigger]');
      trigger?.focus();
    }
  }

  onMobileEscape(event) {
    if (event.key !== 'Escape' || !this.activeSubmenuId) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.closeMobileSubmenu({ restoreFocus: true });
  }

  setMobileMenuMainState(isSubmenuOpen) {
    if (!this.mobileMenuMain) return;

    // Do not set inert or aria-hidden here.
    // The mobile submenus are rendered inside mobileMenuMain,
    // so inerting this parent disables the visible submenu links.
    this.mobileMenuMain.removeAttribute('aria-hidden');
    this.mobileMenuMain.removeAttribute('inert');

    this.mobileMenuMain.classList.toggle('mobile-submenu-is-open', isSubmenuOpen);
  }

  setMobileSubmenuState(submenu, isOpen) {
    if (!submenu) return;

    submenu.classList.toggle('translate-x-full', !isOpen);
    submenu.classList.toggle('translate-x-0', isOpen);

    submenu.style.transform = isOpen ? 'translateX(0)' : 'translateX(100%)';
    submenu.style.display = isOpen ? 'block' : 'none';
    submenu.style.pointerEvents = isOpen ? 'auto' : 'none';
    submenu.style.zIndex = isOpen ? '60' : '0';

    submenu.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

    if (isOpen) {
      submenu.removeAttribute('inert');
    } else {
      submenu.setAttribute('inert', '');
    }
  }

  getMobileSummaryLabel() {
    if (this.activeSubmenuId) return 'Go back';
    if (this.detailsElement?.open) return 'Close main menu';
    return 'Open main menu';
  }

  updateMobileMenuLabel() {
    if (!this.summaryElement) return;

    const label = this.getMobileSummaryLabel();
    this.summaryElement.setAttribute('aria-label', label);

    if (this.summaryLabel) {
      this.summaryLabel.textContent = label;
    }
  }

  focusMobileSubmenu(submenu) {
    if (!submenu) return;

    submenu.focus();
  }

  onClickOutside(event) {
    if (!this.contains(event.target)) {
      this.closeAllDropdowns();
    }
  }

  toggleDropdown(wrapper, trigger, dropdown) {
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';

    this.closeAllDropdowns();

    if (!isOpen) {
      this.openDropdown(wrapper, trigger, dropdown);
    }
  }

  openDropdown(wrapper, trigger, dropdown) {
    if (wrapper.getAttribute('data-open') === 'true') return;

    dropdown.classList.remove('invisible', 'opacity-0');
    dropdown.classList.add('visible', 'opacity-100');
    dropdown.setAttribute('aria-hidden', 'false');
    if (dropdown.classList.contains('max-h-0')) {
      dropdown.style.maxHeight = '500px';
    }
    this.updateAriaExpanded(trigger, true);
    wrapper.setAttribute('data-open', 'true');
  }

  closeDropdown(wrapper, trigger, dropdown) {
    if (wrapper.getAttribute('data-open') !== 'true') return;

    dropdown.classList.add('invisible', 'opacity-0');
    dropdown.classList.remove('visible', 'opacity-100');
    dropdown.setAttribute('aria-hidden', 'true');
    if (dropdown.classList.contains('max-h-0')) {
      dropdown.style.maxHeight = '0px';
    }
    this.updateAriaExpanded(trigger, false);
    wrapper.removeAttribute('data-open');
    wrapper.dispatchEvent(new CustomEvent('mega-menu:dropdown-close', {
      bubbles: true,
      detail: {
        triggerId: trigger.getAttribute('data-mega-menu-trigger'),
      },
    }));
  }

  closeAllDropdowns() {
    this.wrappers.forEach(wrapper => {
      const trigger = wrapper.querySelector('[data-mega-menu-trigger]');
      const dropdown = wrapper.querySelector('[data-mega-menu-dropdown]');
      if (trigger && dropdown) {
        this.closeDropdown(wrapper, trigger, dropdown);
      }
    });
  }

  updateAriaExpanded(trigger, isExpanded) {
    trigger.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    trigger.querySelector('svg')?.classList.toggle('rotate-180', isExpanded);
  }

  openMobileSubmenu(submenuId) {
    const submenu = this.querySelector(`[data-mobile-submenu="${submenuId}"]`);
    if (!submenu) return;

    // Close every mobile submenu first so Support cannot sit over Shop.
    this.querySelectorAll('[data-mobile-submenu]').forEach((panel) => {
      this.setMobileSubmenuState(panel, false);
    });

    this.querySelectorAll('[data-mobile-submenu-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    this.activeSubmenuId = submenuId;
    this.closeAllDropdowns();
    this.setMobileMenuMainState(true);
    this.setMobileSubmenuState(submenu, true);

    const trigger = this.querySelector(`[data-mobile-submenu-trigger="${submenuId}"]`);
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    this.updateMobileMenuIcons();
    this.updateMobileMenuLabel();
    this.focusMobileSubmenu(submenu);
  }

  closeMobileSubmenu({ restoreFocus = false } = {}) {
    if (!this.activeSubmenuId) return;

    const submenuId = this.activeSubmenuId;
    const trigger = this.querySelector(`[data-mobile-submenu-trigger="${submenuId}"]`);

    this.querySelectorAll('[data-mobile-submenu]').forEach((panel) => {
      this.setMobileSubmenuState(panel, false);
    });

    this.querySelectorAll('[data-mobile-submenu-trigger]').forEach((submenuTrigger) => {
      submenuTrigger.setAttribute('aria-expanded', 'false');
    });

    this.activeSubmenuId = null;
    this.setMobileMenuMainState(false);

    if (trigger && restoreFocus) {
      trigger.focus();
    }

    this.updateMobileMenuIcons();
    this.updateMobileMenuLabel();
  }

  updateMobileMenuIcons() {
    if (!this.hamburgerIcon || !this.backIcon) return;

    const showBackButton = Boolean(this.activeSubmenuId);

    this.hamburgerIcon.classList.toggle('hidden', showBackButton);
    this.hamburgerIcon.classList.toggle('flex', !showBackButton);
    this.backIcon.classList.toggle('hidden', !showBackButton);
    this.backIcon.classList.toggle('flex', showBackButton);
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu);
}
